import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { ANALYTICS_API_URL } from '../../../../constants/config';

import { Notification } from '../../../../styledComponents/styledComponents';
import { useQuery } from '@tanstack/react-query';
import { getUnifiedErrorObject } from '../../../../util/ErrorUnifier';
import {
   AddButton,
   AddEmailContainer,
   Card,
   Container,
   EmailAddress,
   EmailItem,
   EmailList,
   EmptyState,
   LogoutButton,
   RemoveButton,
   Section,
   SectionTitle,
   StyledInput,
   SubTitle,
   Tip,
   TipContent,
   TipTitle,
   Title,
   UnsubscribeButton,
} from './AlertConfigComponents';

import * as browserStorage from '../../../../misc/BrowserStorageManager';

function EmailReceiverConfig() {
   const [emailRefresh, triggerEmailRefresh] = useState('start');
   const [emails, setEmails] = useState([]);
   const [errorMessageGET, setErrorMessageGET] = useState('');
   const [emailInput, setEmailInput] = useState('');
   const [emailToBeAdded, setEmailToBeAdded] = useState('');
   const [errorMessageADD, setErrorMessageADD] = useState('');
   const [emailToBeDeleted, setEmailToBeDeleted] = useState('');
   const [errorMessageDELETE, setErrorMessageDELETE] = useState('');
   const [unsubscribeALL, setUnsubscribeALL] = useState(undefined);
   const [errorMessageDeleteAll, setErrorMessageDeleteAll] = useState('');
   const [logout, setLogout] = useState(false);

   const isLimitReached = emails.length >= 2;

   const accessToken = browserStorage.getJwtToken();
   const isAdmin = browserStorage.getUserInfo().isAdmin;

   const header = {
      'x-access-token': accessToken,
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
   };

   useQuery({
      queryKey: [emailRefresh],
      enabled: emailRefresh.length > 0,
      queryFn: () => {
         return axios.get(`${ANALYTICS_API_URL}/v2/alerting/config/email`, { headers: header }).then((res) => res.data);
      },
      onSuccess: (data) => {
         const configuredEmails = data.data.configuredEmailReceivers;
         setEmails(configuredEmails);
         triggerEmailRefresh('');
      },
      onError: (error) => {
         const errorObject = getUnifiedErrorObject(error);
         if (errorObject.errorCode === 101014) {
            const errMsg = errorObject.errorBody.error.userInfo.config;
            setErrorMessageGET(errMsg);
         } else {
            setErrorMessageGET(`Something unexpected happened: ${errorObject.message}`);
         }
      },
      refetchOnWindowFocus: false,
      retry: false,
   });

   const { isLoading: isLoadingADD } = useQuery({
      queryKey: [emailToBeAdded],
      enabled: emailToBeAdded.length > 0,
      queryFn: () =>
         axios.post(
            `${ANALYTICS_API_URL}/v2/alerting/config/email/add`,
            { emailReceiver: emailToBeAdded },
            { headers: header },
         ),
      onSuccess: () => {
         setEmailToBeAdded('');
         setEmailInput('');
         setErrorMessageADD('');
         triggerEmailRefresh(emailToBeAdded);
      },
      onError: (error) => {
         setEmailToBeAdded('');
         const errorObject = getUnifiedErrorObject(error);
         setErrorMessageADD(`Could not add '${emailToBeAdded}': ${errorObject.message}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
   });

   const { isLoading: isLoadingDELETE } = useQuery({
      queryKey: [emailToBeDeleted],
      enabled: emailToBeDeleted.length > 0,
      queryFn: () =>
         axios.delete(`${ANALYTICS_API_URL}/v2/alerting/config/email/delete`, {
            headers: header,
            data: { emailReceiver: emailToBeDeleted },
         }),
      onSuccess: () => {
         setEmailToBeDeleted('');
         setErrorMessageDELETE('');
         triggerEmailRefresh(emailToBeDeleted);
      },
      onError: (error) => {
         setEmailToBeDeleted('');
         const errorObject = getUnifiedErrorObject(error);
         setErrorMessageDELETE(`Could not remove '${emailToBeDeleted}': ${errorObject.message}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
   });

   const { isLoading: isLoadingDeleteALL } = useQuery({
      // queryKey: [unsubscribeALL],
      enabled: unsubscribeALL === true,
      queryFn: () =>
         axios.put(
            `${ANALYTICS_API_URL}/v2/alerting/config/email/replaceAll`,
            { emailReceiver: [] },
            { headers: header },
         ),
      onSuccess: () => {
         setUnsubscribeALL(false);
         setErrorMessageDeleteAll('');
         triggerEmailRefresh('deletedAll');
      },
      onError: (error) => {
         setUnsubscribeALL(false);
         const errorObject = getUnifiedErrorObject(error);
         setErrorMessageDeleteAll(`Could not remove all email receivers: ${errorObject.message}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
   });

   useEffect(() => {
      if (logout) {
         browserStorage.clear(false);
         // history.push(`/${ALERT_CONFIG_VIEW}`);
         document.location.reload();
      }
   }, [logout]);

   const isDeleting =
      (emailToBeDeleted.length > 0 && isLoadingDELETE) || (unsubscribeALL !== undefined && isLoadingDeleteALL);

   const disableADD = emails.length >= 2 || isDeleting;
   const disableDelete = isDeleting || (emailToBeAdded.length > 0 && isLoadingADD);

   return (
      <Container>
         <Card>
            <Title>NanoAlert alert notifications</Title>
            {isAdmin ? (
               <Notification $fontSizePx={13} indentPx={0} className='mt-3'>
                  {
                     'Setting up email receiver is disabled for the admin organization. Please contact the Analytics Team for assistance.'
                  }
               </Notification>
            ) : (
               <>
                  <SubTitle>Manage your email receivers</SubTitle>

                  {errorMessageGET ? (
                     <Notification $fontSizePx={13} indentPx={0}>
                        {errorMessageGET}
                     </Notification>
                  ) : (
                     <>
                        <Section $noBottomMargin={true}>
                           <SectionTitle>Add an email receiver</SectionTitle>
                           <AddEmailContainer>
                              <StyledInput
                                 type='email'
                                 value={emailInput}
                                 onChange={(e) => {
                                    setEmailInput(e.target.value);
                                    setErrorMessageADD('');
                                 }}
                                 placeholder={emails.length >= 2 ? 'Disabled - max. 2 email receivers' : 'Email to add'}
                                 disabled={disableADD}
                              />
                              <AddButton
                                 color='primary'
                                 disabled={emailInput.length === 0 || disableADD}
                                 $opacity={isLimitReached ? 0.5 : 1}
                                 $cursor={isLimitReached ? 'not-allowed' : 'pointer'}
                                 onClick={() => {
                                    if (emails.length < 2) {
                                       setEmailToBeAdded(emailInput.trim());
                                    } else {
                                       setErrorMessageADD('Reached already maximum of 2 email receivers.');
                                    }
                                 }}
                                 size='sm'
                              >
                                 Add
                              </AddButton>
                           </AddEmailContainer>
                           <Notification
                              topMarginPx={-3}
                              $color={errorMessageADD ? undefined : 'white'}
                              $fontSizePx={12}
                              indentPx={0}
                              $minHeight='2.5rem'
                              className='mb-2'
                           >
                              {errorMessageADD ? errorMessageADD : 'fill'}
                           </Notification>
                        </Section>
                        {emails.length > 0 && (
                           <>
                              <Section $noBottomMargin={true}>
                                 <SectionTitle>Configured email receivers</SectionTitle>
                                 <EmailList>
                                    {emails.length === 0 ? (
                                       <EmptyState>No email receivers configured</EmptyState>
                                    ) : (
                                       emails.map((e, index) => (
                                          <EmailItem key={index}>
                                             <EmailAddress>{e}</EmailAddress>
                                             <RemoveButton
                                                onClick={() => setEmailToBeDeleted(e)}
                                                disabled={disableDelete}
                                                size='sm'
                                             >
                                                &times;
                                             </RemoveButton>
                                          </EmailItem>
                                       ))
                                    )}
                                 </EmailList>
                                 <Notification
                                    topMarginPx={5}
                                    $color={errorMessageDELETE ? undefined : 'white'}
                                    $fontSizePx={12}
                                    indentPx={0}
                                    $minHeight='2.5rem'
                                 >
                                    {errorMessageDELETE ? errorMessageDELETE : 'fill'}
                                 </Notification>
                              </Section>
                              <Section>
                                 <SectionTitle>Unsubscribe all email receivers</SectionTitle>
                                 <UnsubscribeButton onClick={() => setUnsubscribeALL(true)} disabled={disableDelete}>
                                    Unsubscribe All
                                 </UnsubscribeButton>
                                 <Notification
                                    topMarginPx={5}
                                    $color={errorMessageDeleteAll ? undefined : 'white'}
                                    $fontSizePx={12}
                                    indentPx={0}
                                 >
                                    {errorMessageDeleteAll ? errorMessageDeleteAll : 'fill'}
                                 </Notification>
                              </Section>
                           </>
                        )}
                        <Tip>
                           <TipTitle>
                              <span role='img' aria-label='tip' aria-labelledby='tip'>
                                 💡
                              </span>{' '}
                              Tip
                           </TipTitle>
                           <TipContent>
                              If you want to send alert notifications to more than two email receivers, consider
                              creating a forwarding rule for one of your email receivers in your email client.
                           </TipContent>
                        </Tip>
                     </>
                  )}
               </>
            )}
            <LogoutButton onClick={() => setLogout(true)}>Logout</LogoutButton>
         </Card>
      </Container>
   );
}

export default EmailReceiverConfig;
